// unmanaged
// /projects/:id — one analysis (docs/project-tracking.md): next action, the
// SOP's 8 milestones, details and team, references, and the log.
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import PageShell from "../PageShell";
import PageContainer from "../components/PageContainer";
import PageHeader from "../components/PageHeader";
import { useAuthStore } from "../store/authStore";
import { userRoles } from "../utils/roles";
import {
  addNote,
  deleteNote,
  deleteProject,
  fetchCandidates,
  fetchCompanyProjects,
  fetchMeta,
  fetchProject,
  linkItems,
  mergeProject,
  setMilestone,
  unlinkItems,
  updateProject,
} from "./projectApi";
import {
  HEALTH,
  PRIORITY_LABEL,
  ROLE_LABEL,
  STATUS_LABEL,
  StageBar,
  formatTime,
  personName,
} from "./projectParts";

function Card({ title, children, sx }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, ...sx }}>
      {title && (
        <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.6, color: "#64748B", mb: 1 }}>
          {title}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

function PersonSelect({ label, value, team, onChange, emptyLabel = "Unassigned" }) {
  return (
    <TextField select size="small" label={label} value={value || ""} onChange={(e) => onChange(e.target.value || null)} fullWidth margin="dense">
      <MenuItem value=""><em>{emptyLabel}</em></MenuItem>
      {(team || []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
    </TextField>
  );
}

function refLabel(ref) {
  if (!ref) return "platform";
  if (ref.type === "input") return `upload ${ref.filename || ref.id}`;
  if (ref.type === "run") return `run ${ref.id}`;
  if (ref.type === "evaluation") return `evaluation of run ${ref.report_id}${ref.variant ? ` (${ref.variant})` : ""}`;
  return "platform";
}

// How many of each linked kind the page lists; the counts link to the rest.
const PREVIEW = 5;

const pick = (obj, fields) => Object.fromEntries(fields.map((f) => [f, obj?.[f] ?? null]));
const NEXT_FIELDS = ["next_action", "next_action_owner", "next_action_due"];
const DETAIL_FIELDS = ["name", "offering", ...Object.keys(ROLE_LABEL), "client_contact"];
const changed = (draft, saved) => Object.keys(draft).some((k) => (draft[k] || null) !== (saved?.[k] || null));

function ConfirmDialog({ open, title, text, confirmLabel, danger, onCancel, onConfirm, children }) {
  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {text && <DialogContentText>{text}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color={danger ? "error" : "primary"} onClick={onConfirm}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const roles = userRoles(user);
  const isAdmin = roles.includes("Admin");
  const isAllowed = isAdmin || roles.includes("InternalUser");

  const [meta, setMeta] = useState(null);
  const [project, setProject] = useState(null);
  const [entries, setEntries] = useState([]);
  const [linked, setLinked] = useState({ inputs: [], runs: [] });
  const [candidates, setCandidates] = useState(null);       // open "Add from company" dialog when set
  const [picked, setPicked] = useState({ inputs: [], runs: [] });
  const [mergeOptions, setMergeOptions] = useState(null);   // open merge dialog when set
  const [mergeFrom, setMergeFrom] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [nextDraft, setNextDraft] = useState({});
  const [detailDraft, setDetailDraft] = useState({});
  const [note, setNote] = useState("");
  const [newRef, setNewRef] = useState({ label: "", url: "" });
  const [closing, setClosing] = useState(false);
  const [closingNote, setClosingNote] = useState("");
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, data] = await Promise.all([fetchMeta(), fetchProject(id)]);
      setMeta(m);
      setProject(data.project);
      setEntries(data.entries || []);
      setLinked({ inputs: data.inputs || [], runs: data.runs || [] });
      setNextDraft(pick(data.project, NEXT_FIELDS));
      setDetailDraft(pick(data.project, DETAIL_FIELDS));
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load the project");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isAllowed) load();
  }, [isAllowed, load]);

  const run = async (fn) => {
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || "That didn't save");
    }
  };
  const save = (patch) => run(() => updateProject(id, patch));
  const toggleMilestone = (key, completed) => {
    if (key === "closed" && completed) {
      setClosing(true);
      return;
    }
    run(() => setMilestone(id, key, completed));
  };
  const closeProject = () => run(async () => {
    if (closingNote.trim()) await addNote(id, closingNote.trim());
    await setMilestone(id, "closed", true);
    setClosing(false);
    setClosingNote("");
  });

  if (!isAllowed) {
    return (
      <PageShell>
        <PageContainer>
          <Alert severity="error" sx={{ mt: 2 }}>Projects is limited to Admin and InternalUser roles.</Alert>
        </PageContainer>
      </PageShell>
    );
  }
  if (loading) {
    return (
      <PageShell>
        <PageContainer><Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box></PageContainer>
      </PageShell>
    );
  }
  if (!project) {
    return (
      <PageShell>
        <PageContainer>
          <Alert severity="error" sx={{ mt: 2 }}>{error || "Project not found"}</Alert>
          <Link component={RouterLink} to="/projects" sx={{ color: "#fff" }}>Back to Projects</Link>
        </PageContainer>
      </PageShell>
    );
  }

  const milestones = project.milestones || {};
  const isClosed = project.status === "closed";
  const overdue = (project.attention || []).includes("overdue");
  const phases = [];
  (meta?.milestones || []).forEach((m) => {
    let phase = phases.find((p) => p.name === m.phase);
    if (!phase) phases.push((phase = { name: m.phase, steps: [] }));
    phase.steps.push(m);
  });
  const references = project.references || [];

  return (
    <PageShell>
      <PageContainer>
        <Link component={RouterLink} to="/projects" sx={{ color: "#CBD5E1", fontSize: "0.85rem" }}>
          ← Projects / {project.company_name || project.company_id}
        </Link>
        <PageHeader icon={<AccountTreeIcon />} title={`${project.id} ${project.name || ""}`} subtitle={project.company_name} />

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Card>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1 }}>
            <TextField select size="small" label="Health" value={project.health || "green"} onChange={(e) => save({ health: e.target.value })}>
              {(meta?.healths || []).map((h) => (
                <MenuItem key={h} value={h}>
                  <Box component="span" sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: HEALTH[h]?.color, mr: 1 }} />
                  {HEALTH[h]?.label || h}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Status" value={project.status} disabled={isClosed}
                       onChange={(e) => save({ status: e.target.value })}
                       helperText={isClosed ? "Un-tick 'Project closed' to reopen" : ""}>
              {(meta?.statuses || []).filter((s) => s !== "closed" || isClosed).map((s) => (
                <MenuItem key={s} value={s}>{STATUS_LABEL[s] || s}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Priority" value={project.priority || "medium"} onChange={(e) => save({ priority: e.target.value })}>
              {(meta?.priorities || []).map((p) => <MenuItem key={p} value={p}>{PRIORITY_LABEL[p] || p}</MenuItem>)}
            </TextField>
          </Box>
        </Card>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2, alignItems: "start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <Card title="Next action" sx={overdue ? { border: "2px solid #DC2626" } : {}}>
              {overdue && <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600, mb: 1 }}>Overdue</Typography>}
              <TextField size="small" label="Next action" value={nextDraft.next_action || ""} fullWidth margin="dense"
                         onChange={(e) => setNextDraft((d) => ({ ...d, next_action: e.target.value }))} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                <PersonSelect label="Owner" value={nextDraft.next_action_owner} team={meta?.team}
                              onChange={(v) => setNextDraft((d) => ({ ...d, next_action_owner: v }))} />
                <TextField size="small" type="date" label="Due" value={nextDraft.next_action_due || ""} margin="dense"
                           InputLabelProps={{ shrink: true }}
                           onChange={(e) => setNextDraft((d) => ({ ...d, next_action_due: e.target.value || null }))} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button variant="contained" size="small" disabled={!changed(nextDraft, project)}
                        onClick={() => save({ ...nextDraft, next_action: nextDraft.next_action || null })}>
                  Save next action
                </Button>
              </Box>
            </Card>

            <Card title="Workflow">
              <StageBar milestones={milestones} defs={meta?.milestones} stage={project.stage} />
              {phases.map((phase) => (
                <Box key={phase.name} sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>{phase.name}</Typography>
                  {phase.steps.map((m) => {
                    const step = milestones[m.key];
                    const blocked = m.key === "closed" && !step && !milestones.presentation_delivered;
                    return (
                      <Box key={m.key} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <Checkbox
                          checked={!!step}
                          disabled={blocked}
                          onChange={(e) => toggleMilestone(m.key, e.target.checked)}
                          inputProps={{ "aria-label": m.label }}
                          sx={{ p: 0.75 }}
                        />
                        <Box sx={{ pt: 0.75, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: step ? 600 : 400 }}>{m.label}</Typography>
                          {step ? (
                            <Typography variant="caption" sx={{ color: step.source === "auto" ? "#2563EB" : "text.secondary" }}>
                              {step.source === "auto"
                                ? `auto · ${formatTime(step.completed_at)} · ${refLabel(step.ref)}`
                                : `${formatTime(step.completed_at)} · ${personName(meta?.team, step.completed_by) || step.completed_by}`}
                            </Typography>
                          ) : blocked ? (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>Only after the presentation is delivered</Typography>
                          ) : m.key === "data_qc" && linked.inputs[0]?.validation_status ? (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Hint: input check {linked.inputs[0].validation_status} on {linked.inputs[0].filename}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Card>

            <Card title="Notes and history">
              <TextField multiline minRows={2} fullWidth size="small" placeholder="Add a note: decisions, delays, customer requests…"
                         value={note} onChange={(e) => setNote(e.target.value)} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button size="small" variant="contained" disabled={!note.trim()}
                        onClick={() => run(async () => { await addNote(id, note.trim()); setNote(""); })}>
                  Add note
                </Button>
              </Box>
              {entries.map((e) => (
                <Box key={e.id} sx={{ borderTop: "1px solid #E2E8F0", py: 1, display: "flex", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {formatTime(e.created)} · {personName(meta?.team, e.actor) || e.actor || "Automatic"}
                    </Typography>
                    <Typography variant="body2" sx={e.kind === "note" ? { whiteSpace: "pre-wrap" } : { color: "text.secondary", fontStyle: "italic" }}>
                      {e.comment}
                    </Typography>
                  </Box>
                  {e.kind === "note" && (isAdmin || e.actor === user?.id) && (
                    <Tooltip title="Delete note">
                      <IconButton size="small" aria-label="Delete note" onClick={() => setNoteToDelete(e)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Card>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <Card title="Details and team">
              <TextField size="small" label="Name" value={detailDraft.name || ""} fullWidth margin="dense"
                         onChange={(e) => setDetailDraft((d) => ({ ...d, name: e.target.value }))} />
              <TextField select size="small" label="Offering" value={detailDraft.offering || ""} fullWidth margin="dense"
                         onChange={(e) => setDetailDraft((d) => ({ ...d, offering: e.target.value || null }))}>
                <MenuItem value=""><em>Not set</em></MenuItem>
                {(meta?.offerings || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
              {Object.entries(ROLE_LABEL).map(([field, label]) => (
                <PersonSelect key={field} label={label} value={detailDraft[field]} team={meta?.team}
                              onChange={(v) => setDetailDraft((d) => ({ ...d, [field]: v }))} />
              ))}
              <TextField size="small" label="Client contact" value={detailDraft.client_contact || ""} fullWidth margin="dense"
                         helperText="A customer user's email, or a name"
                         onChange={(e) => setDetailDraft((d) => ({ ...d, client_contact: e.target.value }))} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button variant="contained" size="small" disabled={!changed(detailDraft, project)}
                        onClick={() => save({ ...detailDraft, client_contact: detailDraft.client_contact || null })}>
                  Save details
                </Button>
              </Box>
            </Card>

            <Card title="Data and results">
              {/* The counts open the full sets: every user's, with the usual actions. */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
                <Link component={RouterLink} to={`/report-list?project=${encodeURIComponent(id)}`} sx={{ fontWeight: 600 }}>
                  {linked.inputs.length} input file{linked.inputs.length === 1 ? "" : "s"}
                </Link>
                <Link component={RouterLink} to={`/report-list?project=${encodeURIComponent(id)}#runs`} sx={{ fontWeight: 600 }}>
                  {linked.runs.length} run{linked.runs.length === 1 ? "" : "s"}
                </Link>
              </Box>
              {linked.inputs.length === 0 && linked.runs.length === 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Nothing in this project yet. Choose it when uploading or starting a run, or link existing work below.
                </Typography>
              )}
              {(linked.inputs.length > PREVIEW || linked.runs.length > PREVIEW) && (
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  The {PREVIEW} newest of each; the counts above open them all.
                </Typography>
              )}
              {linked.inputs.slice(0, PREVIEW).map((i) => (
                <Box key={i.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{i.filename}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Upload · {i.user_id} · {formatTime(i.upload_date)}{i.validation_status ? ` · check ${i.validation_status}` : ""}
                    </Typography>
                  </Box>
                  <IconButton size="small" aria-label={`Unlink ${i.filename}`}
                              onClick={() => run(() => unlinkItems(id, { inputIds: [i.id] }))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {linked.runs.slice(0, PREVIEW).map((r) => (
                <Box key={r.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link component={RouterLink} to={`/report-list/${encodeURIComponent(r.user_id)}/${encodeURIComponent(r.id)}`}>
                      Run {r.id}
                    </Link>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                      {r.product || "—"} · {r.status || "queued"} · {r.user_id}
                    </Typography>
                  </Box>
                  <IconButton size="small" aria-label={`Unlink run ${r.id}`}
                              onClick={() => run(() => unlinkItems(id, { runIds: [r.id] }))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                <Button size="small" onClick={() => run(async () => {
                  setPicked({ inputs: [], runs: [] });
                  setCandidates(await fetchCandidates(id));
                })}>
                  Add from {project.company_name || "company"}
                </Button>
                <Button size="small" onClick={() => run(async () => {
                  const res = await fetchCompanyProjects(project.company_id);
                  setMergeFrom("");
                  setMergeOptions((res.records || []).filter((p) => p.id !== project.id));
                })}>
                  Merge a duplicate in
                </Button>
              </Box>
            </Card>

            <Card title="References">
              {references.length === 0 && <Typography variant="body2" sx={{ color: "text.secondary" }}>No links yet.</Typography>}
              {references.map((r, i) => (
                <Box key={`${r.url}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Link href={r.url} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                    {r.label || r.url}
                  </Link>
                  <IconButton size="small" aria-label={`Remove ${r.label || r.url}`}
                              onClick={() => save({ references: references.filter((_, j) => j !== i) })}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr auto" }, gap: 1, mt: 1, alignItems: "center" }}>
                <TextField size="small" label="Label" value={newRef.label} onChange={(e) => setNewRef((r) => ({ ...r, label: e.target.value }))} />
                <TextField size="small" label="URL" value={newRef.url} onChange={(e) => setNewRef((r) => ({ ...r, url: e.target.value }))} />
                <Button size="small" disabled={!/^https?:\/\//i.test(newRef.url.trim())}
                        onClick={() => run(async () => {
                          await updateProject(id, { references: [...references, { label: newRef.label.trim(), url: newRef.url.trim() }] });
                          setNewRef({ label: "", url: "" });
                        })}>
                  Add
                </Button>
              </Box>
            </Card>

            {isAdmin && (
              <Card title="Admin">
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Cancel a project with its status. Deleting is for test data only; its ID is never reused.
                </Typography>
                <Button color="error" variant="outlined" size="small" onClick={() => setConfirmDelete(true)}>Delete project</Button>
              </Card>
            )}
          </Box>
        </Box>
      </PageContainer>

      <ConfirmDialog
        open={closing}
        title={`Close ${project.id}?`}
        confirmLabel="Close project"
        onCancel={() => setClosing(false)}
        onConfirm={closeProject}
      >
        <DialogContentText>
          Presentation delivered {formatTime(milestones.presentation_delivered?.completed_at)}.
          Check that the final results and presentation are filed under References before closing.
        </DialogContentText>
        <TextField multiline minRows={2} fullWidth margin="normal" label="Closing note (optional)"
                   value={closingNote} onChange={(e) => setClosingNote(e.target.value)} />
      </ConfirmDialog>
      <ConfirmDialog
        open={!!candidates}
        title={`Add to ${project.id}`}
        text={`Uploads and runs in no project yet: ${project.company_name || "the company"}'s users', and Metalytics staff's from the last 90 days.`}
        confirmLabel="Link selected"
        onCancel={() => setCandidates(null)}
        onConfirm={() => run(async () => {
          await linkItems(id, { inputIds: picked.inputs, runIds: picked.runs });
          setCandidates(null);
        })}
      >
        {candidates && candidates.inputs.length === 0 && candidates.runs.length === 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>Nothing unlinked.</Typography>
        )}
        {[["inputs", candidates?.inputs || [], (i) => `${i.filename} · ${i.user_id} · ${formatTime(i.upload_date)}`],
          ["runs", candidates?.runs || [], (r) => `Run ${r.id} · ${r.product || "—"} · ${r.status || "queued"} · ${r.user_id}`]]
          .map(([kind, items, label]) => items.map((item) => (
            <Box key={`${kind}-${item.id}`} sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={picked[kind].includes(item.id)}
                inputProps={{ "aria-label": label(item) }}
                onChange={(e) => setPicked((p) => ({
                  ...p,
                  [kind]: e.target.checked ? [...p[kind], item.id] : p[kind].filter((x) => x !== item.id),
                }))}
              />
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{label(item)}</Typography>
            </Box>
          )))}
      </ConfirmDialog>
      <ConfirmDialog
        open={!!mergeOptions}
        title={`Merge into ${project.id}`}
        text="Moves the other project's uploads and runs here, then cancels it. Use it when colleagues tagged the same work two ways."
        confirmLabel="Merge"
        onCancel={() => setMergeOptions(null)}
        onConfirm={() => mergeFrom && run(async () => {
          await mergeProject(id, mergeFrom);
          setMergeOptions(null);
        })}
      >
        <TextField select fullWidth margin="normal" label="Project to merge in" value={mergeFrom}
                   onChange={(e) => setMergeFrom(e.target.value)}>
          {(mergeOptions || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.id} {p.name}</MenuItem>)}
        </TextField>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!noteToDelete}
        title="Delete note?"
        text={noteToDelete?.comment}
        confirmLabel="Delete"
        danger
        onCancel={() => setNoteToDelete(null)}
        onConfirm={() => run(async () => { await deleteNote(noteToDelete.id); setNoteToDelete(null); })}
      />
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${project.id}?`}
        text="This removes the project and its log. Use status Cancelled for a real project."
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await deleteProject(id);
            navigate("/projects");
          } catch (e) {
            setConfirmDelete(false);
            setError(e?.message || "Could not delete");
          }
        }}
      />
    </PageShell>
  );
}
